import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Cog, BarChart3, ArrowUpRight, ArrowRight } from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const cards = [
  {
    icon: UserPlus,
    tag: "RPO",
    title: "Recruitment Process Outsourcing",
    body: "AI-assisted sourcing and screening, run by recruiters who make every final call — faster shortlists without lower quality.",
    to: "/services/rpo",
    accent: "#00E5FF",
  },
  {
    icon: Cog,
    tag: "BPO",
    title: "Business Process Outsourcing",
    body: "Customer support, admin, and industry-specific back-office work handled by trained specialists, with AI-assisted tools speeding up the routine parts.",
    to: "/services/bpo",
    accent: "#A3FF5E",
  },
  {
    icon: BarChart3,
    tag: "KPO",
    title: "Knowledge Process Outsourcing",
    body: "Data processing, research, and analytics turned into something you can act on — not just a bigger spreadsheet.",
    to: "/services/kpo",
    accent: "#007AFF",
  },
];

export default function ServicesOverview() {
  return (
    <>
      <PageMeta
        title="Services — A4 Assist"
        description="Three ways to work with A4 Assist: RPO (recruitment), BPO (operations), and KPO (data & research) — AI-enabled workflows run by trained specialists."
      />
      <section className="pt-36 pb-16 sm:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="00"
            eyebrow="Services"
            title={
              <>
                Three ways to{" "}
                <span className="font-serif italic">work</span> with us.
              </>
            }
            subtitle="You might need one role filled, one function covered, or a completely new capability you don't want to build in-house. A4 Assist works across three service lines, and most clients start with one and expand as they grow."
          />
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.tag}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-2xl border border-slate-800 bg-gradient-to-b from-[#0A1325] to-[#070F1F] p-8 hover:border-[#00E5FF]/50 transition-all overflow-hidden flex flex-col"
              data-testid={`services-card-${c.tag.toLowerCase()}`}
            >
              <div
                className="absolute -top-24 -right-24 h-56 w-56 rounded-full opacity-[0.10] blur-3xl"
                style={{ background: c.accent }}
              />
              <div className="flex items-start justify-between">
                <div
                  className="inline-flex h-12 w-12 items-center justify-center rounded-xl border"
                  style={{
                    borderColor: `${c.accent}40`,
                    background: `${c.accent}12`,
                    color: c.accent,
                  }}
                >
                  <c.icon size={22} />
                </div>
                <span
                  className="font-display text-xs font-bold tracking-[0.24em] uppercase"
                  style={{ color: c.accent }}
                >
                  {c.tag}
                </span>
              </div>
              <h3 className="font-display mt-8 text-2xl font-semibold text-white">
                {c.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400 flex-1">
                {c.body}
              </p>
              <Link
                to={c.to}
                className="group/link mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#00E5FF]"
              >
                Learn more <ArrowUpRight size={16} className="arrow-shift" />
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
          <div>
            <h3 className="font-display text-xl font-semibold text-white">
              Not sure where to start?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
              Book a free 30-minute audit and we'll help you figure out
              which service line makes sense for where you are today.
            </p>
          </div>
          <a
            href={calendlyUrl()}
            {...calendlyLinkProps}
            data-testid="services-audit-cta"
            className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
          >
            Get My Free Audit
            <ArrowRight size={16} className="arrow-shift" />
          </a>
        </div>
      </section>
    </>
  );
}
